import { DaisychainStep } from './types/daisychain-step';
import { slugify } from './strings';
import { smoothScrollToElement } from './jquery';

/**
 * Set up daisychain actions on thank you page.
 */
export const daisychainActions = () => {
  const daisychainRows = document.querySelectorAll('.alz-daisychain-row');
  if (!daisychainRows.length) return;

  const daisychainSteps: DaisychainStep[] = [];

  // First, check if there's a previously done step to show in the daisychain steps indicator - e.g. 'Signed' or 'Emailed'

  const previouslyDoneStepName = document.querySelector('.alz-daisychain-previous-step-name');
  const previouslyDoneStepNameText = previouslyDoneStepName?.textContent?.trim() ?? '';

  if (previouslyDoneStepNameText) {
    daisychainSteps.push({
      done: true,
      name: previouslyDoneStepNameText,
      rowIndex: -1, // This step isn't in the daisychain rows, so give it a row index of -1 to indicate this
      slug: slugify(previouslyDoneStepNameText),
    });
  }

  // Next, iterate through each daisychain row to populate the other steps

  daisychainRows.forEach((row, index) => {
    const stepNameElement = row.querySelector('.alz-daisychain-step-name');
    const name = stepNameElement?.textContent?.trim() ?? undefined;
    const slug = name ? slugify(name) : `daisychain-step-${index}`;

    daisychainSteps.push({ name, rowIndex: index, slug });

    // Update row ID so we can navigate to it later
    row.id = slug;
  });

  console.log('Daisychain steps:', daisychainSteps);

  // Now we know all the steps, update any action and skip buttons to link to the right step and update step indicator
  updateActionAndSkipButtons(daisychainRows, daisychainSteps);

  // Add the daisychain steps indicator based on the information we've gathered
  addStepsIndicator(daisychainSteps);

  // Hide the steps indicator when footer is in view to prevent overlap
  showHideStepsIndicator();
};

/**
 * Add steps indicator at bottom of screen.
 *
 * If you have a daisychain row that shouldn't be included in the steps indicator, e.g. a final thank you message, simply don't include a name (element with the class 'alz-daisychain-step-name') in that row.
 */
const addStepsIndicator = (daisychainSteps: DaisychainStep[]) => {
  document.body.insertAdjacentHTML(
    'beforeend',
    `
      <div aria-hidden="true" class="alz-daisychain-steps-indicator">
        <div class="alz-daisychain-steps-indicator-inner">
          ${daisychainSteps
            .map((step) =>
              step.name
                ? `
                <div class="alz-daisychain-steps-indicator-step ${step.done ? 'alz-daisychain-steps-indicator-step-done' : ''} ${step.skipped ? 'alz-daisychain-steps-indicator-step-skipped' : ''}" data-alz-daisychain-slug="${step.slug}">
                  ${step.name}
                </div>
              `
                : '',
            )
            .join('')}
        </div>
      </div>
    `,
  );
};

// Hide the steps indicator when footer is in view (otherwise ugly overlap)
const showHideStepsIndicator = (): (() => void) => {
  const indicator = document.querySelector<HTMLElement>('.alz-daisychain-steps-indicator');
  const footer = document.querySelector<HTMLElement>('footer');

  if (!indicator || !footer) return () => {};

  const observer = new IntersectionObserver(
    ([entry]) => {
      indicator.classList.toggle('alz-daisychain-steps-indicator-hidden', entry.isIntersecting);
    },
    { threshold: 0 },
  );

  observer.observe(footer);

  return () => observer.disconnect();
};

// Update skip buttons targets and styling in daisychain rows
const updateActionAndSkipButtons = (
  daisychainRows: NodeListOf<Element>,
  daisychainSteps: DaisychainStep[],
) => {
  daisychainRows.forEach((row, index) => {
    const actionButtons = row.querySelectorAll(
      '.alz-buttons a, a.alz-button, a.en__socialShare, button.alz-share-mobile-button',
    ) as NodeListOf<HTMLAnchorElement>;

    actionButtons.forEach((actionButton) => {
      const isSkipButton = actionButton.getAttribute('href') === '#skip';
      const currentStep = daisychainSteps.find((step) => step.rowIndex === index);
      const nextStep = daisychainSteps.find((step) => step.rowIndex === index + 1);

      // Add secondary button styling to skip buttons and set their href to the next step in the daisychain
      if (isSkipButton) {
        actionButton.classList.add('alz-button-secondary');
        if (nextStep) actionButton.href = `#${nextStep.slug}`;
      }

      // Add event listener to smoothly scroll to the next step when skip button is clicked
      actionButton.addEventListener('click', (event) => {
        const currentStepIndicator = currentStep
          ? document.querySelector(
              `.alz-daisychain-steps-indicator-step[data-alz-daisychain-slug="${currentStep.slug}"]`,
            )
          : null;

        if (currentStepIndicator)
          currentStepIndicator.classList.add(
            isSkipButton
              ? 'alz-daisychain-steps-indicator-step-skipped'
              : 'alz-daisychain-steps-indicator-step-done',
          );

        const nextStepRow = nextStep ? document.getElementById(nextStep.slug) : null;

        if (nextStepRow) {
          // Show the next step row
          nextStepRow.classList.add('alz-daisychain-row-show');

          // Prevent skip button from jumping to the next step - we will scroll there smoothly instead
          if (isSkipButton) event.preventDefault();
          smoothScrollToElement(nextStepRow);

          // Keep hash locations in history so user can use back button to navigate to previous step
          history.pushState(null, '', `#${nextStepRow.id}`);
        }
      });
    });
  });
};
